import { fetchAuthSession } from "aws-amplify/auth";
import { COMPETITION_API_BASE } from "./competitionApiEnvironment";

const API_BASE = COMPETITION_API_BASE;

const matchProgramPath = (matchId, family) => `/competition/matches/${encodeURIComponent(matchId)}/program?family=${encodeURIComponent(family)}`;
export async function getMatchProgram(matchId, family, download = false) {
  return (await request(`${matchProgramPath(matchId, family)}${download ? "&download=1" : ""}`)).data;
}
export async function prepareMatchProgram(matchId, family, body) {
  return (await request(matchProgramPath(matchId, family), { auth: true, method: "POST", body: JSON.stringify(body) })).data;
}
export async function saveMatchProgram(matchId, family, body, remove = false) {
  return (await request(matchProgramPath(matchId, family), { auth: true, method: remove ? "DELETE" : "PUT", body: JSON.stringify(body) })).data;
}
const LOCAL_TEST_AUTH_TOKEN =
  process.env.REACT_APP_ENABLE_COMPETITION_TEST_AUTH === "true"
    ? String(process.env.REACT_APP_COMPETITION_TEST_AUTH_TOKEN || "").trim()
    : "";

export function isCompetitionTestAuthEnabled() {
  return Boolean(LOCAL_TEST_AUTH_TOKEN);
}

async function getAuthHeaders(required = false) {
  if (LOCAL_TEST_AUTH_TOKEN) {
    return { authorization: `Bearer ${LOCAL_TEST_AUTH_TOKEN}` };
  }

  try {
    const session = await fetchAuthSession();
    const token =
      session?.tokens?.idToken?.toString() ||
      session?.tokens?.accessToken?.toString() ||
      "";

    if (token) {
      return { authorization: `Bearer ${token}` };
    }
  } catch {
    // Fall through to required check below.
  }

  if (required) {
    throw new Error("Sign in required");
  }

  return {};
}

async function request(path, options = {}) {
  const { auth = false, headers: providedHeaders = {}, ...fetchOptions } = options;
  const authHeaders = await getAuthHeaders(auth);
  const retryableRead = ["GET", "HEAD"].includes(String(fetchOptions.method || "GET").toUpperCase());
  for (let attempt = 0; ; attempt += 1) {
    const response = await fetch(`${API_BASE}${path}`, {
      headers: {
        "content-type": "application/json",
        ...authHeaders,
        ...providedHeaders
      },
      ...fetchOptions
    });
    const payload = await response.json().catch(() => ({}));
    if (response.ok) return payload;

    // Retry only safe reads; score/registration mutations must never be replayed implicitly.
    if (retryableRead && attempt < 2 && [429, 502, 503, 504].includes(response.status)) {
      const retryAfter = response.headers.get("retry-after");
      const requestedDelay = !retryAfter ? 0 : Number.isFinite(Number(retryAfter))
        ? Math.max(0, Number(retryAfter) * 1000)
        : Math.max(0, Date.parse(retryAfter) - Date.now());
      const delay = Math.max(500 * 2 ** attempt + Math.floor(Math.random() * 250), requestedDelay || 0);
      if (delay <= 5000) {
        await new Promise(resolve => setTimeout(resolve, delay));
        if (fetchOptions.signal?.aborted) throw new DOMException("Request aborted", "AbortError");
        continue;
      }
    }
    const error = new Error(payload?.error?.message || `Request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
}

async function requestAllPages(pathBase, { auth = false, pageLimit = 100, maxPages = 50, requireComplete = false } = {}) {
  const all = [];
  let nextToken = null;
  let pageCount = 0;
  const seen = new Set();

  do {
    const params = new URLSearchParams();
    params.set("limit", String(pageLimit));
    if (nextToken) {
      params.set("nextToken", nextToken);
    }

    const separator = pathBase.includes("?") ? "&" : "?";
    const payload = await request(`${pathBase}${separator}${params.toString()}`, { auth });
    all.push(...(payload.data || []));
    nextToken = payload?.meta?.nextToken || null;
    pageCount += 1;
    if (requireComplete && nextToken && (seen.has(nextToken) || pageCount >= maxPages)) throw new Error("Not all score pages could be loaded. Refresh to try again.");
    if (nextToken) seen.add(nextToken);
  } while (nextToken && pageCount < maxPages);

  return all;
}

export async function searchPublicMatchesPage({ query = "", area, nextToken, signal, ...filters } = {}) {
  const params = new URLSearchParams({ discovery: "1", limit: "50", ...filters });
  if (query) params.set("q", query);
  if (nextToken) params.set("nextToken", nextToken);
  if (area) Object.entries(area).forEach(([key, value]) => params.set(key, String(value)));
  const payload = await request(`/matches?${params}`, { signal });
  return { data: payload.data || [], nextToken: payload.meta?.nextToken || null, locations: payload.meta?.locations || [] };
}

export async function listMatchesPage({
  limit = 100,
  nextToken = null,
  status = null,
  publicationState = null,
  q = null,
  sortDir = "desc"
} = {}) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("sortDir", sortDir);
  if (nextToken) {
    params.set("nextToken", nextToken);
  }
  if (status) {
    params.set("status", status);
  }
  if (publicationState) {
    params.set("publicationState", publicationState);
  }
  if (q && String(q).trim()) {
    params.set("q", String(q).trim());
  }

  const payload = await request(`/matches?${params.toString()}`);
  return {
    data: payload.data || [],
    nextToken: payload?.meta?.nextToken || null,
    meta: payload?.meta || {}
  };
}

export async function listBullseyeMatchesPage({
  limit = 100,
  nextToken = null,
  status = null,
  publicationState = null,
  q = null,
  sortDir = "desc"
} = {}) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("sortDir", sortDir);
  if (nextToken) {
    params.set("nextToken", nextToken);
  }
  if (status) {
    params.set("status", status);
  }
  if (publicationState) {
    params.set("publicationState", publicationState);
  }
  if (q && String(q).trim()) {
    params.set("q", String(q).trim());
  }

  const payload = await request(`/bullseye/matches?${params.toString()}`);
  return {
    data: payload.data || [],
    nextToken: payload?.meta?.nextToken || null,
    meta: payload?.meta || {}
  };
}

export async function listUSPSAMatchesPage({
  limit = 100,
  nextToken = null,
  status = null,
  publicationState = null,
  q = null,
  sortDir = "desc"
} = {}) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("sortDir", sortDir);
  if (nextToken) {
    params.set("nextToken", nextToken);
  }
  if (status) {
    params.set("status", status);
  }
  if (publicationState) {
    params.set("publicationState", publicationState);
  }
  if (q && String(q).trim()) {
    params.set("q", String(q).trim());
  }

  const payload = await request(`/uspsa/matches?${params.toString()}`);
  return {
    data: payload.data || [],
    nextToken: payload?.meta?.nextToken || null,
    meta: payload?.meta || {}
  };
}

export async function getMatch(matchId, { auth = false } = {}) {
  const payload = await request(`/matches/${encodeURIComponent(matchId)}`, { auth });
  return payload.data;
}

export async function getCompetitionAuditPage(matchId, discipline, { nextToken, signal } = {}) {
  const params = new URLSearchParams({ discipline });
  if (nextToken) params.set("nextToken", nextToken);
  const payload = await request(`/competition/matches/${encodeURIComponent(matchId)}/audit?${params}`, {
    auth: true, cache: "no-store", signal,
  });
  return payload.data;
}

export async function getBullseyeMatch(matchId, { auth = false } = {}) {
  const payload = await request(`/bullseye/matches/${encodeURIComponent(matchId)}`, { auth });
  return payload.data;
}

export async function getUSPSAMatch(matchId, { auth = false } = {}) {
  const payload = await request(`/uspsa/matches/${encodeURIComponent(matchId)}`, { auth });
  return payload.data;
}

export async function listTournamentsPage({
  limit = 100,
  nextToken = null,
  visibility = "public",
  status = null,
  owned = false,
  sortDir = "asc"
} = {}) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("sortDir", sortDir);
  if (visibility) {
    params.set("visibility", visibility);
  }
  if (status) {
    params.set("status", status);
  }
  if (owned) {
    params.set("owned", "true");
  }
  if (nextToken) {
    params.set("nextToken", nextToken);
  }

  const payload = await request(`/tournaments?${params.toString()}`, { auth: owned });
  return {
    data: payload.data || [],
    nextToken: payload?.meta?.nextToken || null,
    meta: payload?.meta || {}
  };
}

export async function getTournament(tournamentId, { auth = false } = {}) {
  const payload = await request(`/tournaments/${encodeURIComponent(tournamentId)}`, { auth });
  return payload.data;
}

export async function listTournamentEvents(tournamentId, { auth = false } = {}) {
  return requestAllPages(`/tournaments/${encodeURIComponent(tournamentId)}/events`, { auth });
}

export async function createTournamentEvent(tournamentId, body = {}) {
  const payload = await request(`/tournaments/${encodeURIComponent(tournamentId)}/events`, {
    method: "POST",
    auth: true,
    body: JSON.stringify(body || {})
  });
  return payload.data;
}

export async function updateTournamentEvent(tournamentId, eventLinkId, body = {}) {
  const payload = await request(
    `/tournaments/${encodeURIComponent(tournamentId)}/events/${encodeURIComponent(eventLinkId)}`,
    {
      method: "PATCH",
      auth: true,
      body: JSON.stringify(body || {})
    }
  );
  return payload.data;
}

export async function detachTournamentEvent(tournamentId, eventLinkId) {
  return (await request(`/tournaments/${encodeURIComponent(tournamentId)}/events/${encodeURIComponent(eventLinkId)}`, {
    method: "DELETE", auth: true,
  })).data;
}

export async function listTournamentLeaderboard(tournamentId, { auth = false } = {}) {
  const payload = await request(`/tournaments/${encodeURIComponent(tournamentId)}/leaderboard`, { auth });
  return payload.data || [];
}

export async function listTournamentResults(tournamentId, { auth = false, eligibleOnly = false } = {}) {
  const params = new URLSearchParams();
  if (eligibleOnly) {
    params.set("eligibleOnly", "true");
  }
  const query = params.toString();
  return requestAllPages(`/tournaments/${encodeURIComponent(tournamentId)}/results${query ? `?${query}` : ""}`, { auth });
}

export async function importTournamentArcJson(tournamentId, body = {}) {
  const payload = await request(`/tournaments/${encodeURIComponent(tournamentId)}/imports/arc`, {
    method: "POST",
    auth: true,
    body: JSON.stringify(body || {})
  });
  return payload.data;
}

export async function listMyTournamentParticipants({ status = null } = {}) {
  const params = new URLSearchParams();
  params.set("limit", "100");
  if (status) {
    params.set("status", status);
  }
  return requestAllPages(`/tournaments/participants/mine?${params.toString()}`, { auth: true });
}

export async function listTournamentParticipants(tournamentId, { status = null } = {}) {
  const params = new URLSearchParams();
  params.set("limit", "100");
  if (status) {
    params.set("status", status);
  }
  return requestAllPages(`/tournaments/${encodeURIComponent(tournamentId)}/participants?${params.toString()}`, { auth: true, pageLimit: 100 });
}

export async function requestTournamentParticipation(tournamentId, body = {}) {
  const payload = await request(`/tournaments/${encodeURIComponent(tournamentId)}/participants/request`, {
    method: "POST",
    auth: true,
    body: JSON.stringify(body || {})
  });
  return payload.data;
}

export async function updateTournamentParticipant(tournamentId, participantUserId, body = {}) {
  const payload = await request(
    `/tournaments/${encodeURIComponent(tournamentId)}/participants/${encodeURIComponent(participantUserId)}`,
    {
      method: "PATCH",
      auth: true,
      body: JSON.stringify(body || {})
    }
  );
  return payload.data;
}

export async function recalculateTournamentLeaderboard(tournamentId) {
  const payload = await request(`/tournaments/${encodeURIComponent(tournamentId)}/recalculate`, {
    method: "POST",
    auth: true,
    body: JSON.stringify({})
  });
  return payload;
}

export async function createTournament(body = {}) {
  const payload = await request("/tournaments", {
    method: "POST",
    auth: true,
    body: JSON.stringify(body || {})
  });
  return payload.data;
}

export async function previewTournamentPolicy(tournamentId, body) {
  return (await request(`/tournaments/${encodeURIComponent(tournamentId)}/scoring-policy/preview`, {
    method: "POST", auth: true, body: JSON.stringify(body), cache: "no-store",
  })).data;
}

export async function applyTournamentPolicy(tournamentId, body) {
  return request(`/tournaments/${encodeURIComponent(tournamentId)}/scoring-policy/apply`, {
    method: "POST", auth: true, body: JSON.stringify(body), cache: "no-store",
  });
}

export async function getTournamentStandingsReview(tournamentId) {
  return getTournamentStandingsSnapshot(tournamentId, { director: true });
}

export async function getTournamentStandingsSnapshot(tournamentId, { director = false } = {}) {
  const endpoint = director ? "standings-review" : "leaderboard";
  const rows = [], cursors = new Set();
  let cursor = null, meta = null;
  do {
    const params = new URLSearchParams({ limit: "100" });
    if (cursor) params.set("nextToken", cursor);
    const page = await request(`/tournaments/${encodeURIComponent(tournamentId)}/${endpoint}?${params}`, { auth: director, cache: "no-store" });
    if (!Array.isArray(page.data)) throw new Error("Standings response was incomplete. Refresh to try again.");
    if (meta && meta.generationId !== page.meta?.generationId) throw new Error("Standings changed while loading. Refresh to load the new standings.");
    meta = { ...meta, ...page.meta };
    rows.push(...page.data);
    cursor = page.meta?.nextToken || null;
    if (cursor && (cursors.has(cursor) || cursors.size >= 99)) throw new Error("Not all standings could be loaded. Refresh to try again.");
    if (cursor) cursors.add(cursor);
  } while (cursor);
  return { data: rows, meta: { ...meta, count: rows.length, nextToken: null } };
}

export async function updateTournament(tournamentId, body = {}) {
  const payload = await request(`/tournaments/${encodeURIComponent(tournamentId)}`, {
    method: "PATCH",
    auth: true,
    body: JSON.stringify(body || {})
  });
  return payload.data;
}

export async function createMatch(body = {}) {
  const payload = await request("/matches", {
    method: "POST",
    auth: true,
    body: JSON.stringify(body || {})
  });
  return payload.data;
}

export async function createBullseyeMatch(body = {}) {
  const payload = await request("/bullseye/matches", {
    method: "POST",
    auth: true,
    body: JSON.stringify(body || {})
  });
  return payload.data;
}

export async function createUSPSAMatch(body = {}) {
  const payload = await request("/uspsa/matches", {
    method: "POST",
    auth: true,
    body: JSON.stringify(body || {})
  });
  return payload.data;
}

export async function updateMatch(matchId, body = {}) {
  const payload = await request(`/matches/${encodeURIComponent(matchId)}`, {
    method: "PATCH",
    auth: true,
    body: JSON.stringify(body || {})
  });
  return payload.data;
}

export async function updateBullseyeMatch(matchId, body = {}) {
  const payload = await request(`/bullseye/matches/${encodeURIComponent(matchId)}`, {
    method: "PATCH",
    auth: true,
    body: JSON.stringify(body || {})
  });
  return payload.data;
}

export async function updateUSPSAMatch(matchId, body = {}) {
  const payload = await request(`/uspsa/matches/${encodeURIComponent(matchId)}`, {
    method: "PATCH",
    auth: true,
    body: JSON.stringify(body || {})
  });
  return payload.data;
}

export async function createLeaderboardBannerUploadUrl(matchId, { contentType = "image/png" } = {}) {
  const payload = await request("/uploads/leaderboard-banner/presign", {
    method: "POST",
    auth: true,
    body: JSON.stringify({
      matchId,
      contentType
    })
  });
  return payload.data;
}

export async function createTournamentLeaderboardBannerUploadUrl(tournamentId, { contentType = "image/png" } = {}) {
  const payload = await request(`/tournaments/${encodeURIComponent(tournamentId)}/leaderboard-banner/presign`, {
    method: "POST",
    auth: true,
    body: JSON.stringify({
      contentType
    })
  });
  return payload.data;
}

export async function getMatchRegistrationSummary(matchId) {
  const payload = await request(`/competition/matches/${encodeURIComponent(matchId)}/registration-summary`);
  return payload.data;
}

export async function getRelatedMatches(matchId, family) {
  try {
    return (await request(`/competition/matches/${encodeURIComponent(matchId)}/related-matches?family=${encodeURIComponent(family)}`)).data;
  } catch (error) {
    // New web clients can precede the additive backend route. Other failures
    // must not silently turn a related-match entry into a standalone one.
    if (error.status === 404) return null;
    throw error;
  }
}

export async function createCombinedMatchRegistration(matchId, family, body) {
  return (await request(`/competition/matches/${encodeURIComponent(matchId)}/registrations/batch?family=${encodeURIComponent(family)}`,
    { method: "POST", auth: true, body: JSON.stringify(body) })).data;
}

export async function copyMainRosterRegistration(matchId, family, body) {
  return (await request(`/competition/matches/${encodeURIComponent(matchId)}/registrations/from-main?family=${encodeURIComponent(family)}`,
    { method: "POST", auth: true, body: JSON.stringify(body) })).data;
}

export async function saveSideMatchRelationship(parentId, childId, family, body, detach = false) {
  return (await request(`/competition/matches/${encodeURIComponent(parentId)}/side-matches/${encodeURIComponent(childId)}?family=${encodeURIComponent(family)}`,
    { method: detach ? "DELETE" : "PUT", auth: true, body: JSON.stringify(body) })).data;
}

export async function getBullseyeMatchRegistrationSummary(matchId) {
  const payload = await request(`/competition/matches/${encodeURIComponent(matchId)}/registration-summary`);
  return payload.data;
}

export async function getUSPSAMatchRegistrationSummary(matchId) {
  const payload = await request(`/competition/matches/${encodeURIComponent(matchId)}/registration-summary`);
  return payload.data;
}

export async function getMyBullseyeMatchRegistration(matchId) {
  const payload = await request(`/competition/matches/${encodeURIComponent(matchId)}/my-registration`, {
    auth: true
  });
  return payload.data ? normalizeCanonicalRegistration(payload.data, "bullseye") : null;
}

export async function getMyUSPSAMatchRegistration(matchId) {
  const payload = await request(`/competition/matches/${encodeURIComponent(matchId)}/my-registration`, {
    auth: true
  });
  return payload.data ? normalizeCanonicalRegistration(payload.data, "uspsa") : null;
}

export async function getMatchSetupDetail(matchId) {
  const payload = await request(`/matches/${encodeURIComponent(matchId)}/setup-detail`);
  return payload.data;
}

export async function getBullseyeMatchSetupDetail(matchId, { auth = false } = {}) {
  const payload = await request(`/bullseye/matches/${encodeURIComponent(matchId)}/setup-detail`, { auth });
  return payload.data;
}

export async function getBullseyeRelayRoster(matchId, { signal } = {}) {
  const payload = await request(`/bullseye/matches/${encodeURIComponent(matchId)}/relay-roster`, { signal, cache: "no-store" });
  // Older API deployments can route an unknown suffix to the generic match read.
  // Do not mistake that response for a successfully loaded empty roster.
  if (!Array.isArray(payload.data?.competitors) || !Array.isArray(payload.data?.groups)) {
    throw new Error("Relay roster is unavailable. Please try again later.");
  }
  return payload.data;
}

export async function getUSPSAMatchSetupDetail(matchId, { auth = false } = {}) {
  const payload = await request(`/uspsa/matches/${encodeURIComponent(matchId)}/setup-detail`, { auth });
  return payload.data;
}

export async function getStageSetupDetail(matchId, stageId) {
  const payload = await request(`/matches/${encodeURIComponent(matchId)}/stages/${encodeURIComponent(stageId)}/setup-detail`);
  return payload.data;
}

export async function getUSPSAStageSetupDetail(matchId, stageId) {
  const payload = await request(`/uspsa/matches/${encodeURIComponent(matchId)}/stages/${encodeURIComponent(stageId)}/setup-detail`);
  return payload.data;
}

export async function listMatchLeaderboards({ matchId, squadId = null, squadNumber = null, includeAllEntries = null, requireComplete = false } = {}) {
  const query = buildQuery({ matchId, squadId, squadNumber, includeAllEntries });
  return requestAllPages(`/match-leaderboards?${query}`, { requireComplete });
}

export async function listBullseyeMatchLeaderboards({ matchId, requireComplete = false } = {}) {
  const query = buildQuery({ matchId });
  return requestAllPages(`/bullseye/match-leaderboards?${query}`, { requireComplete });
}

export async function listBullseyeMatchLeaderboardsPage({
  matchId,
  limit = 50,
  nextToken = null
} = {}) {
  const query = buildQuery({ matchId, limit, nextToken });
  const payload = await request(`/bullseye/match-leaderboards?${query}`);
  return {
    data: payload.data || [],
    nextToken: payload?.meta?.nextToken || null,
    meta: payload?.meta || {}
  };
}

export async function listBullseyeTeamLeaderboards({ matchId } = {}) {
  const query = buildQuery({ matchId });
  return requestAllPages(`/bullseye/team-leaderboards?${query}`);
}

export async function listOwnedUSPSAMatches(
  userId,
  {
    limit = 50,
    nextToken = null,
    status = null,
    publicationState = null,
    q = null,
    sortDir = "desc"
  } = {}
) {
  const targetUserId = String(userId || "").trim();
  if (!targetUserId) {
    throw new Error("Missing user identity");
  }

  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("sortDir", sortDir);
  if (nextToken) {
    params.set("nextToken", nextToken);
  }
  if (status) {
    params.set("status", status);
  }
  if (publicationState) {
    params.set("publicationState", publicationState);
  }
  if (q && String(q).trim()) {
    params.set("q", String(q).trim());
  }

  const payload = await request(`/users/${encodeURIComponent(targetUserId)}/uspsa-matches/owned?${params.toString()}`, {
    auth: true
  });

  return {
    data: payload.data || [],
    nextToken: payload?.meta?.nextToken || null,
    meta: payload?.meta || {}
  };
}

export async function listStageLeaderboards({ matchId, squadId = null, squadNumber = null, includeAllEntries = null, requireComplete = false } = {}) {
  const query = buildQuery({ matchId, squadId, squadNumber, includeAllEntries });
  return requestAllPages(`/stage-leaderboards?${query}`, { requireComplete });
}

export async function getMatchScoreDetail(matchId, sk) {
  const payload = await request(`/match-leaderboards/${encodeURIComponent(matchId)}/${encodeURIComponent(sk)}/score-detail`);
  return payload.data;
}

export async function getStageScoreDetail(matchStageId, sk) {
  const payload = await request(`/stage-leaderboards/${encodeURIComponent(matchStageId)}/${encodeURIComponent(sk)}/score-detail`);
  return payload.data;
}


function buildQuery(paramsObj = {}) {
  const params = new URLSearchParams();
  Object.entries(paramsObj).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return;
    }
    const text = String(value).trim();
    if (!text) {
      return;
    }
    params.set(key, text);
  });
  return params.toString();
}

export async function listInstructorSlots({
  instructorId,
  from = null,
  to = null,
  status = null,
  privateClassId = null,
  sortDir = "asc",
  pageLimit = 100,
  maxPages = 50
} = {}) {
  const target = String(instructorId || "").trim();
  if (!target) {
    throw new Error("Instructor ID is required");
  }

  const query = buildQuery({ from, to, status, privateClassId, sortDir });
  const pathBase = `/instructors/${encodeURIComponent(target)}/slots${query ? `?${query}` : ""}`;
  return requestAllPages(pathBase, { pageLimit, maxPages });
}

export async function updateInstructorSlotStatus({ instructorId, slotId, status }) {
  const targetInstructor = String(instructorId || "").trim();
  const targetSlot = String(slotId || "").trim();
  const nextStatus = String(status || "").trim().toLowerCase();
  if (!targetInstructor) {
    throw new Error("Instructor ID is required");
  }
  if (!targetSlot) {
    throw new Error("Slot ID is required");
  }
  if (!nextStatus) {
    throw new Error("Slot status is required");
  }

  const payload = await request(
    `/instructors/${encodeURIComponent(targetInstructor)}/slots/${encodeURIComponent(targetSlot)}`,
    {
      method: "PATCH",
      body: JSON.stringify({ status: nextStatus })
    }
  );
  return payload.data;
}

export async function deleteInstructorSlot({ instructorId, slotId }) {
  const targetInstructor = String(instructorId || "").trim();
  const targetSlot = String(slotId || "").trim();
  if (!targetInstructor) {
    throw new Error("Instructor ID is required");
  }
  if (!targetSlot) {
    throw new Error("Slot ID is required");
  }

  const payload = await request(
    `/instructors/${encodeURIComponent(targetInstructor)}/slots/${encodeURIComponent(targetSlot)}`,
    { method: "DELETE" }
  );
  return payload.data;
}

export async function listInstructorBookings({
  instructorId,
  status = null,
  privateClassId = null,
  sortDir = "asc",
  pageLimit = 100,
  maxPages = 50
} = {}) {
  const target = String(instructorId || "").trim();
  if (!target) {
    throw new Error("Instructor ID is required");
  }

  const query = buildQuery({ status, privateClassId, sortDir });
  const pathBase = `/instructors/${encodeURIComponent(target)}/bookings${query ? `?${query}` : ""}`;
  return requestAllPages(pathBase, { pageLimit, maxPages });
}

export async function listStudentBookings({
  studentId,
  status = null,
  privateClassId = null,
  sortDir = "asc",
  pageLimit = 100,
  maxPages = 50
} = {}) {
  const target = String(studentId || "").trim();
  if (!target) {
    throw new Error("Student ID is required");
  }

  const query = buildQuery({ status, privateClassId, sortDir });
  const pathBase = `/students/${encodeURIComponent(target)}/bookings${query ? `?${query}` : ""}`;
  return requestAllPages(pathBase, { pageLimit, maxPages });
}
export { API_BASE };

export async function getMyCompetitionPerson() {
  const payload = await request("/people/me", { auth: true });
  return payload.data || null;
}

export async function updateMyCompetitionPerson(body = {}) {
  const payload = await request("/people/me", {
    method: "PATCH",
    auth: true,
    body: JSON.stringify(body || {})
  });
  return payload.data || null;
}

export async function suggestMatchVenues(query, { latitude, longitude, signal } = {}) {
  const params = new URLSearchParams({ q: String(query || "").trim() });
  if (latitude !== undefined && latitude !== null && latitude !== "" && longitude !== undefined && longitude !== null && longitude !== "") {
    params.set("latitude", String(latitude));
    params.set("longitude", String(longitude));
  }
  const payload = await request(`/competition/venues/suggestions?${params}`, { auth: true, signal });
  if (!Array.isArray(payload?.data?.items)) throw new Error("Venue suggestions could not be loaded. Enter the venue details manually or try again.");
  return payload.data;
}

export async function getMatchVenue(placeId, { signal } = {}) {
  const params = new URLSearchParams({ placeId: String(placeId || "") });
  const payload = await request(`/competition/venues/place?${params}`, { auth: true, signal });
  if (!payload?.data?.venue) throw new Error("This venue could not be loaded. Enter the venue details manually or try again.");
  return payload.data;
}

export async function createCompetitionRegistration(matchId, body = {}) {
  const payload = await request(`/competition/matches/${encodeURIComponent(matchId)}/registrations`, {
    method: "POST",
    auth: true,
    body: JSON.stringify(body || {})
  });
  return payload.data;
}

export async function listCompetitionRegistrations(matchId) {
  const payload = await request(`/competition/matches/${encodeURIComponent(matchId)}/registrations`, {
    auth: true
  });
  return payload.data || [];
}

export async function confirmCompetitionRegistration(matchId, registrationId, body = {}) {
  const payload = await request(
    `/competition/matches/${encodeURIComponent(matchId)}/registrations/${encodeURIComponent(registrationId)}/confirm`,
    { method: "POST", auth: true, body: JSON.stringify(body || {}) }
  );
  return payload.data;
}

export async function updateCompetitionRegistration(matchId, registrationId, body = {}) {
  const payload = await request(
    `/competition/matches/${encodeURIComponent(matchId)}/registrations/${encodeURIComponent(registrationId)}`,
    { method: "PATCH", auth: true, body: JSON.stringify(body || {}) }
  );
  return payload.data;
}

export async function cancelCompetitionRegistration(matchId, registrationId, expectedUpdatedAt) {
  const payload = await request(
    `/competition/matches/${encodeURIComponent(matchId)}/registrations/${encodeURIComponent(registrationId)}/cancel`,
    { method: "POST", auth: true, body: JSON.stringify({ expectedUpdatedAt }) }
  );
  return payload.data;
}

export async function listCompetitionParticipants(matchId) {
  const payload = await request(`/competition/matches/${encodeURIComponent(matchId)}/participants`, {
    auth: true
  });
  return payload.data || [];
}

export async function listCompetitionEntries(matchId) {
  const payload = await request(`/competition/matches/${encodeURIComponent(matchId)}/entries`, {
    auth: true
  });
  return payload.data || [];
}

export async function updateCompetitionEntry(matchId, entryId, body = {}) {
  if (typeof body.expectedUpdatedAt !== "string" || !body.expectedUpdatedAt.trim()) {
    throw new Error("Refresh this scoring entry before updating it.");
  }
  const payload = await request(
    `/competition/matches/${encodeURIComponent(matchId)}/entries/${encodeURIComponent(entryId)}`,
    { method: "PATCH", auth: true, body: JSON.stringify(body) }
  );
  return payload.data;
}

export async function listCompetitionResults(matchId, { auth = false, includeAllEntries = null } = {}) {
  const query = buildQuery({ includeAllEntries });
  const payload = await request(`/competition/matches/${encodeURIComponent(matchId)}/results${query ? `?${query}` : ""}`, { auth });
  return payload.data || [];
}

export async function listCompetitionOutcomes(matchId, { status = null, auth = true, includeAudit = null } = {}) {
  const query = buildQuery({ status, includeAudit });
  const payload = await request(`/competition/matches/${encodeURIComponent(matchId)}/outcomes${query ? `?${query}` : ""}`, { auth });
  return payload.data || [];
}

export async function createCompetitionOutcome(matchId, participantId, body = {}) {
  const payload = await request(
    `/competition/matches/${encodeURIComponent(matchId)}/participants/${encodeURIComponent(participantId)}/outcomes`,
    {
      method: "POST",
      auth: true,
      body: JSON.stringify(body || {})
    }
  );
  return payload.data;
}

export async function clearCompetitionOutcome(matchId, participantId, outcomeId) {
  const payload = await request(
    `/competition/matches/${encodeURIComponent(matchId)}/participants/${encodeURIComponent(participantId)}/outcomes/${encodeURIComponent(outcomeId)}`,
    {
      method: "DELETE",
      auth: true
    }
  );
  return payload.data;
}

export async function upsertCompetitionResult(matchId, body = {}) {
  const payload = await request(`/competition/matches/${encodeURIComponent(matchId)}/results`, {
    method: "POST",
    auth: true,
    body: JSON.stringify(body || {})
  });
  return payload.data;
}

function buildMembership(system, memberId, label) {
  const value = String(memberId || "").trim();
  if (!value) {
    return null;
  }
  return { system, memberId: value, label };
}

function findMembershipValue(source, systems) {
  const memberships = Array.isArray(source?.membershipSnapshot)
    ? source.membershipSnapshot
    : Array.isArray(source?.memberships)
      ? source.memberships
      : [];
  const expected = new Set(systems.map((system) => String(system || "").toLowerCase()));
  const match = memberships.find((membership) => expected.has(String(membership?.system || "").toLowerCase()));
  return String(match?.memberId || "").trim();
}

function normalizeCanonicalRegistration(row, fallbackDiscipline = "") {
  if (!row || typeof row !== "object") {
    return row;
  }

  const profile = row.disciplineProfile && typeof row.disciplineProfile === "object" ? row.disciplineProfile : {};
  const categories = Array.isArray(row.categories)
    ? row.categories
    : Array.isArray(profile.categories)
      ? profile.categories
      : [];
  const affiliations = Array.isArray(row.affiliations)
    ? row.affiliations
    : Array.isArray(profile.affiliations)
      ? profile.affiliations
      : [];
  const firearmChoices = Array.isArray(profile.firearmChoices)
    ? profile.firearmChoices
    : Array.isArray(row.firearmChoices)
      ? row.firearmChoices
      : [];
  const isBullseye = String(row.discipline || fallbackDiscipline).toLowerCase() === "bullseye";
  const nraCompetitorId = String(isBullseye
    ? profile.bullseyeNraCompetitorId ?? row.bullseyeNraCompetitorId ?? findMembershipValue(row, ["nra-competitor"])
    : profile.bullseyeNraCompetitorId || profile.nraIdNumber || findMembershipValue(row, ["nra-competitor"])
  ).trim();
  const nraMemberId = String(profile.nraMemberId ?? row.nraMemberId ?? findMembershipValue(row, ["nra"])).trim();
  const uspsaMemberNumber = String(profile.uspsaMemberNumber || findMembershipValue(row, ["uspsa"])).trim();
  const competitorId = String(row.legacyCompetitorId || row.competitorId || row.registrationId || "").trim();

  return {
    ...profile,
    ...row,
    discipline: row.discipline || fallbackDiscipline,
    registrationId: row.registrationId || competitorId,
    competitorId,
    participantId: row.participantId || "",
    personId: row.personId || "",
    primaryEntryId: row.primaryEntryId || "",
    entryId: row.primaryEntryId || row.entryId || "",
    entryIds: Array.isArray(row.entryIds) ? row.entryIds : [],
    displayName: row.displayName || profile.displayName || "",
    email: row.email || profile.email || "",
    competitorNumber: row.competitorNumber || profile.competitorNumber || "",
    classification: row.classification || profile.classification || "",
    division: row.division || profile.division || profile.divisionId || "",
    divisionId: row.division || profile.divisionId || profile.division || "",
    categories,
    affiliations,
    category: profile.category || categories[0] || "",
    firearmChoices,
    nraMemberId,
    isDistinguished: (row.isDistinguished ?? profile.isDistinguished) === true,
    nraPrimaryMemberId: profile.nraPrimaryMemberId || "",
    nraIdNumber: isBullseye ? undefined : nraCompetitorId || nraMemberId,
    bullseyeNraCompetitorId: nraCompetitorId,
    uspsaMemberNumber,
    memberId: profile.memberId || "",
    powerFactor: profile.powerFactor || "",
    teamName: profile.teamName || "",
    phoneNumber: profile.phoneNumber || "",
    dateOfBirth: profile.dateOfBirth || "",
    notes: profile.notes || "",
    squadId: profile.squadId || row.squadId || "",
    squadNumber: profile.squadNumber ?? row.squadNumber,
    squadScheduledAt: profile.squadScheduledAt || row.squadScheduledAt || "",
    relayLabel: profile.relayLabel || row.relayLabel || "",
    firingPointLabel: profile.firingPointLabel || row.firingPointLabel || ""
  };
}

function normalizeCanonicalRegistrationResult(result, fallbackDiscipline = "") {
  const row = result?.registration || result;
  const normalized = normalizeCanonicalRegistration(row, fallbackDiscipline);
  if (result?.participant) {
    normalized.participantId = result.participant.participantId;
  }
  if (Array.isArray(result?.entries)) {
    normalized.entries = result.entries;
    normalized.entryId = result.entries[0]?.entryId || normalized.entryId;
  }
  return normalized;
}

function filterRegistrationsByStatus(rows, status) {
  const target = String(status || "").trim().toLowerCase();
  if (!target) {
    return rows;
  }
  return rows.filter((row) => String(row?.status || "").trim().toLowerCase() === target);
}

function withCompetitionRegistrationFields(body = {}, discipline) {
  const normalizedDiscipline = String(discipline || "").trim().toLowerCase();
  const nraCompetitorSource = normalizedDiscipline === "bullseye"
    ? body.bullseyeNraCompetitorId
    : body.bullseyeNraCompetitorId || body.nraIdNumber;
  const nraMemberSource = normalizedDiscipline === "bullseye"
    ? body.nraMemberId
    : body.nraMemberId || body.nraIdNumber;
  const usesExplicitDisciplineSnapshot = normalizedDiscipline === "bullseye" || normalizedDiscipline === "uspsa";
  const memberships = [
    buildMembership("nra", nraMemberSource, "NRA Member ID"),
    buildMembership("nra-competitor", nraCompetitorSource, "NRA Competitor ID"),
    buildMembership("uspsa", body.uspsaMemberNumber, "USPSA Number")
  ].filter(Boolean);

  return {
    ...body,
    discipline,
    nraIdNumber: normalizedDiscipline === "bullseye" ? undefined : body.nraIdNumber,
    legacyCompetitorId: body.competitorId,
    memberships: memberships.length > 0 ? memberships : usesExplicitDisciplineSnapshot ? [] : body.memberships,
    disciplineProfile: {
      ...(body.disciplineProfile || {}),
      divisionId: body.divisionId,
      division: body.division,
      classification: body.classification,
      categories: body.categories,
      affiliations: body.affiliations,
      firearmChoices: body.firearmChoices,
      squadId: body.squadId,
      squadNumber: body.squadNumber,
      squadScheduledAt: body.squadScheduledAt,
      relayLabel: body.relayLabel,
      firingPointLabel: body.firingPointLabel,
      nraMemberId: body.nraMemberId,
      nraPrimaryMemberId: body.nraPrimaryMemberId,
      isDistinguished: body.isDistinguished,
      nraIdNumber: normalizedDiscipline === "bullseye" ? undefined : body.nraIdNumber,
      bullseyeNraCompetitorId: body.bullseyeNraCompetitorId,
      uspsaMemberNumber: body.uspsaMemberNumber,
      powerFactor: body.powerFactor,
      category: body.category,
      teamName: body.teamName,
      memberId: body.memberId,
      phoneNumber: body.phoneNumber,
      dateOfBirth: body.dateOfBirth,
      notes: body.notes,
      firstName: body.firstName, middleNameOrInitial: body.middleNameOrInitial, lastName: body.lastName, suffix: body.suffix,
      streetAddress1: body.streetAddress1, streetAddress2: body.streetAddress2, city: body.city, state: body.state, zipCode: body.zipCode,
      aggregateParticipation: body.aggregateParticipation
    }
  };
}

function requireCanonicalRegistrationId(registrationId) {
  const target = String(registrationId || "").trim();
  if (!target) {
    throw new Error("Registration ID is required");
  }
  return target;
}

export async function createMatchRegistration(matchId, body) {
  const { squadScheduledAt, ...selfEntry } = body;
  return normalizeCanonicalRegistration(
    await createCompetitionRegistration(matchId, withCompetitionRegistrationFields(selfEntry, "arc")),
    "arc"
  );
}

export async function createUSPSAMatchRegistration(matchId, body) {
  return normalizeCanonicalRegistration(
    await createCompetitionRegistration(matchId, withCompetitionRegistrationFields(body, "uspsa")),
    "uspsa"
  );
}

export async function listAllMatchRegistrations(matchId, { status = null, pageLimit = 100, maxPages = 50 } = {}) {
  void pageLimit;
  void maxPages;
  return filterRegistrationsByStatus(
    (await listCompetitionRegistrations(matchId)).map((row) => normalizeCanonicalRegistration(row, "arc")),
    status
  );
}

export async function listUSPSAMatchRegistrations(matchId, { status = null, limit = 100, nextToken = null } = {}) {
  void limit;
  void nextToken;
  const rows = filterRegistrationsByStatus(
    (await listCompetitionRegistrations(matchId)).map((row) => normalizeCanonicalRegistration(row, "uspsa")),
    status
  );
  return {
    data: rows,
    nextToken: null,
    meta: {}
  };
}

export async function listAllUSPSAMatchRegistrations(matchId, { status = null, pageLimit = 100, maxPages = 50 } = {}) {
  void pageLimit;
  void maxPages;
  return filterRegistrationsByStatus(
    (await listCompetitionRegistrations(matchId)).map((row) => normalizeCanonicalRegistration(row, "uspsa")),
    status
  );
}

export async function getMyMatchRegistration(matchId, userId) {
  void userId;
  const payload = await request(`/competition/matches/${encodeURIComponent(matchId)}/my-registration`, {
    auth: true
  });
  return payload.data ? normalizeCanonicalRegistration(payload.data, "arc") : null;
}


export async function confirmMatchRegistration(matchId, registrationId, body = {}) {
  const canonicalRegistrationId = requireCanonicalRegistrationId(registrationId);
  return normalizeCanonicalRegistrationResult(
    await confirmCompetitionRegistration(matchId, canonicalRegistrationId, body),
    "arc"
  );
}

export async function createOwnerManagedMatchRegistration(matchId, body = {}) {
  const registration = await createCompetitionRegistration(matchId, {
    ...withCompetitionRegistrationFields(body, "arc"),
    source: "director"
  });
  return normalizeCanonicalRegistrationResult(
    await confirmCompetitionRegistration(matchId, registration.registrationId, { ...body, expectedUpdatedAt: registration.updatedAt }),
    "arc"
  );
}

export async function createOwnerManagedUSPSAMatchRegistration(matchId, body = {}) {
  const registration = await createCompetitionRegistration(matchId, {
    ...withCompetitionRegistrationFields(body, "uspsa"),
    source: "director"
  });
  return normalizeCanonicalRegistrationResult(
    await confirmCompetitionRegistration(matchId, registration.registrationId, { ...body, expectedUpdatedAt: registration.updatedAt }),
    "uspsa"
  );
}

export async function updateMatchRegistration(matchId, registrationId, body) {
  const canonicalRegistrationId = requireCanonicalRegistrationId(registrationId);
  return normalizeCanonicalRegistration(
    await updateCompetitionRegistration(matchId, canonicalRegistrationId, withCompetitionRegistrationFields(body, "arc")),
    "arc"
  );
}

export async function updateUSPSAMatchRegistration(matchId, registrationId, body) {
  const canonicalRegistrationId = requireCanonicalRegistrationId(registrationId);
  return normalizeCanonicalRegistration(
    await updateCompetitionRegistration(matchId, canonicalRegistrationId, withCompetitionRegistrationFields(body, "uspsa")),
    "uspsa"
  );
}

export async function cancelMatchRegistration(matchId, registrationId, expectedUpdatedAt) {
  const canonicalRegistrationId = requireCanonicalRegistrationId(registrationId);
  if (!expectedUpdatedAt) throw new Error("Refresh this registration before cancelling it.");
  return normalizeCanonicalRegistration(await cancelCompetitionRegistration(matchId, canonicalRegistrationId, expectedUpdatedAt), "arc");
}

export async function confirmUSPSAMatchRegistration(matchId, registrationId, body = {}) {
  const canonicalRegistrationId = requireCanonicalRegistrationId(registrationId);
  return normalizeCanonicalRegistrationResult(
    await confirmCompetitionRegistration(matchId, canonicalRegistrationId, body),
    "uspsa"
  );
}

export async function cancelUSPSAMatchRegistration(matchId, registrationId) {
  const canonicalRegistrationId = requireCanonicalRegistrationId(registrationId);
  return normalizeCanonicalRegistration(await cancelCompetitionRegistration(matchId, canonicalRegistrationId), "uspsa");
}
export async function listUserMatchHistory(
  userId,
  { limit = 25, nextToken = null, statuses = null, includeScores = true } = {}
) {
  const target = String(userId || "").trim();
  if (!target) {
    throw new Error("User ID is required");
  }

  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("includeScores", includeScores ? "true" : "false");
  if (nextToken) {
    params.set("nextToken", nextToken);
  }
  if (Array.isArray(statuses) && statuses.length > 0) {
    params.set("statuses", statuses.join(","));
  }

  const payload = await request(`/users/${encodeURIComponent(target)}/matches/history?${params.toString()}`, {
    auth: true
  });
  return {
    data: payload.data || [],
    nextToken: payload?.meta?.nextToken || null,
    meta: payload?.meta || {}
  };
}

export async function listUserBullseyeMatchHistory(
  userId,
  { limit = 25, nextToken = null, statuses = null, includeScores = true } = {}
) {
  const target = String(userId || "").trim();
  if (!target) {
    throw new Error("User ID is required");
  }

  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("includeScores", includeScores ? "true" : "false");
  if (nextToken) {
    params.set("nextToken", nextToken);
  }
  if (Array.isArray(statuses) && statuses.length > 0) {
    params.set("statuses", statuses.join(","));
  }

  const payload = await request(`/users/${encodeURIComponent(target)}/bullseye-matches/history?${params.toString()}`, {
    auth: true
  });
  return {
    data: payload.data || [],
    nextToken: payload?.meta?.nextToken || null,
    meta: payload?.meta || {}
  };
}

export async function listUserUSPSAMatchHistory(
  userId,
  { limit = 25, nextToken = null, statuses = null, includeScores = true } = {}
) {
  const target = String(userId || "").trim();
  if (!target) {
    throw new Error("User ID is required");
  }

  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("includeScores", includeScores ? "true" : "false");
  if (nextToken) {
    params.set("nextToken", nextToken);
  }
  if (Array.isArray(statuses) && statuses.length > 0) {
    params.set("statuses", statuses.join(","));
  }

  const payload = await request(`/users/${encodeURIComponent(target)}/uspsa-matches/history?${params.toString()}`, {
    auth: true
  });
  return {
    data: payload.data || [],
    nextToken: payload?.meta?.nextToken || null,
    meta: payload?.meta || {}
  };
}

export async function listOwnedMatches(
  userId,
  { limit = 50, nextToken = null, status = null, publicationState = null, q = null, sortDir = "desc" } = {}
) {
  const target = String(userId || "").trim();
  if (!target) {
    throw new Error("User ID is required");
  }

  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("sortDir", sortDir);
  if (status) {
    params.set("status", status);
  }
  if (publicationState) {
    params.set("publicationState", publicationState);
  }
  if (q && String(q).trim()) {
    params.set("q", String(q).trim());
  }
  if (nextToken) {
    params.set("nextToken", nextToken);
  }

  const payload = await request(`/users/${encodeURIComponent(target)}/matches/owned?${params.toString()}`, {
    auth: true
  });
  return {
    data: payload.data || [],
    nextToken: payload?.meta?.nextToken || null,
    meta: payload?.meta || {}
  };
}

export async function listOwnedBullseyeMatches(
  userId,
  { limit = 50, nextToken = null, status = null, publicationState = null, q = null, sortDir = "desc" } = {}
) {
  const target = String(userId || "").trim();
  if (!target) {
    throw new Error("User ID is required");
  }

  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("sortDir", sortDir);
  if (status) {
    params.set("status", status);
  }
  if (publicationState) {
    params.set("publicationState", publicationState);
  }
  if (q && String(q).trim()) {
    params.set("q", String(q).trim());
  }
  if (nextToken) {
    params.set("nextToken", nextToken);
  }

  const payload = await request(`/users/${encodeURIComponent(target)}/bullseye-matches/owned?${params.toString()}`, {
    auth: true
  });
  return {
    data: payload.data || [],
    nextToken: payload?.meta?.nextToken || null,
    meta: payload?.meta || {}
  };
}

export async function listAllBullseyeMatchRegistrations(matchId, { status = null } = {}) {
  return filterRegistrationsByStatus(
    (await listCompetitionRegistrations(matchId)).map((row) => normalizeCanonicalRegistration(row, "bullseye")),
    status
  );
}

export async function createBullseyeMatchRegistration(matchId, body = {}) {
  const { categoriesText, competitorNumber, relayLabel, firingPointLabel, teamName, ...selfEntry } = body;
  return normalizeCanonicalRegistration(
    await createCompetitionRegistration(matchId, withCompetitionRegistrationFields(selfEntry, "bullseye")),
    "bullseye"
  );
}

export async function confirmBullseyeMatchRegistration(matchId, registrationId, body = {}) {
  const canonicalRegistrationId = requireCanonicalRegistrationId(registrationId);
  return normalizeCanonicalRegistrationResult(
    await confirmCompetitionRegistration(matchId, canonicalRegistrationId, body),
    "bullseye"
  );
}

export async function cancelBullseyeMatchRegistration(matchId, registrationId, expectedUpdatedAt) {
  const canonicalRegistrationId = requireCanonicalRegistrationId(registrationId);
  if (!expectedUpdatedAt) throw new Error("Refresh this registration before cancelling it.");
  return normalizeCanonicalRegistration(await cancelCompetitionRegistration(matchId, canonicalRegistrationId, expectedUpdatedAt), "bullseye");
}

export async function createOwnerManagedBullseyeMatchRegistration(matchId, body = {}) {
  const registration = await createCompetitionRegistration(matchId, {
    ...withCompetitionRegistrationFields(body, "bullseye"),
    source: "director"
  });
  return normalizeCanonicalRegistrationResult(
    await confirmCompetitionRegistration(matchId, registration.registrationId, { ...body, expectedUpdatedAt: registration.updatedAt }),
    "bullseye"
  );
}

export async function updateBullseyeMatchRegistration(matchId, registrationId, body = {}) {
  const canonicalRegistrationId = requireCanonicalRegistrationId(registrationId);
  return normalizeCanonicalRegistration(
    await updateCompetitionRegistration(matchId, canonicalRegistrationId, withCompetitionRegistrationFields(body, "bullseye")),
    "bullseye"
  );
}

// Authenticated, independently versioned web content and registration controls.
export async function getDirectorPortal(matchId, family) {
  const payload = await request(`/competition/matches/${encodeURIComponent(matchId)}/director?family=${encodeURIComponent(family)}`, { auth: true });
  return payload.data;
}
export async function getMatchUpdates(matchId, family, { review = false, nextToken } = {}) {
  const params = new URLSearchParams({ family });
  if (review) params.set("mode", "review");
  if (nextToken) params.set("nextToken", nextToken);
  return request(`/competition/matches/${encodeURIComponent(matchId)}/updates?${params}`, { auth: review });
}
export async function publishMatchUpdate(matchId, family, body) {
  return (await request(`/competition/matches/${encodeURIComponent(matchId)}/updates?family=${encodeURIComponent(family)}`, { auth: true, method: "POST", body: JSON.stringify(body) })).data;
}
export async function getMyUpdates(nextToken) {
  return request(`/competition/updates${nextToken ? `?nextToken=${encodeURIComponent(nextToken)}` : ""}`, { auth: true });
}
export async function markUpdatesRead(body) {
  return (await request("/competition/updates", { auth: true, method: "PATCH", body: JSON.stringify(body) })).data;
}
export async function listMatchCreationDrafts() {
  return (await request("/competition/match-drafts", { auth: true })).data;
}
export async function saveMatchCreationDraft(body) {
  return (await request("/competition/match-drafts", { auth: true, method: "PATCH", body: JSON.stringify(body) })).data;
}
export async function saveDirectorPortal(matchId, family, body) {
  const payload = await request(`/competition/matches/${encodeURIComponent(matchId)}/director?family=${encodeURIComponent(family)}`, {
    auth: true, method: "PATCH", body: JSON.stringify(body),
  });
  return payload.data;
}


const waitlistPath = (matchId, family) => `/competition/matches/${encodeURIComponent(matchId)}/waitlist?family=${encodeURIComponent(family)}`;
export async function getMatchWaitlist(matchId, family, director = false) {
  return (await request(`${waitlistPath(matchId, family)}${director ? "&view=director" : ""}`, { auth: true })).data;
}
export async function joinMatchWaitlist(matchId, family, body) {
  return (await request(waitlistPath(matchId, family), { auth: true, method: "POST", body: JSON.stringify(body) })).data;
}
export async function changeMatchWaitlist(matchId, family, body) {
  return (await request(waitlistPath(matchId, family), { auth: true, method: "PATCH", body: JSON.stringify(body) })).data;
}
export async function getMyWaitlists() {
  return (await request("/competition/waitlist", { auth: true })).data;
}

const registrationInvitationPath = (matchId, family, resource = "invitations") => `/competition/matches/${encodeURIComponent(matchId)}/${resource}?family=${encodeURIComponent(family)}`;
export async function listRegistrationInvitations(matchId, family) {
  return (await request(registrationInvitationPath(matchId, family), { auth: true })).data;
}
export async function createRegistrationInvitation(matchId, family, body) {
  return (await request(registrationInvitationPath(matchId, family), { auth: true, method: "POST", body: JSON.stringify(body) })).data;
}
export async function revokeRegistrationInvitation(matchId, family, body) {
  return (await request(registrationInvitationPath(matchId, family), { auth: true, method: "PATCH", body: JSON.stringify(body) })).data;
}
export async function getRegistrationInvitationAccess(matchId, family) {
  return (await request(registrationInvitationPath(matchId, family, "invitation-access"), { auth: true })).data;
}
export async function redeemRegistrationInvitationAccess(matchId, family, body) {
  return (await request(registrationInvitationPath(matchId, family, "invitation-access"), { auth: true, method: "POST", body: JSON.stringify(body) })).data;
}
export async function saveRegistrationDefaults(registrationDefaults) {
  return updateMyCompetitionPerson({ registrationDefaults });
}

export async function updateMyMatchRegistration(matchId, registrationId, family, body) {
  const { source, discipline, squadScheduledAt, ...selfEntry } = body;
  return normalizeCanonicalRegistration(await updateCompetitionRegistration(matchId, registrationId, selfEntry), family);
}
